import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { BranchBadge } from './BranchSelector';
import { ROLE_LABELS } from '../utils/permissions';
import { getAuthHeaders } from './dashboardUtils';

function StatTile({ label, value }) {
  return (
    <div className="rounded-lg border border-gray-700/40 bg-gray-800/60 p-3">
      <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">{label}</div>
      <div className="font-mono text-xl font-semibold text-white">{value}</div>
    </div>
  );
}

function StaffPerformanceDetail({ staff, dateRange }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!staff) return;
    setLoading(true);
    fetch(`/api/jobs?start_date=${dateRange.start}&end_date=${dateRange.end}`, {
      headers: getAuthHeaders(),
    })
      .then(r => r.ok ? r.json() : { jobs: [] })
      .then(data => {
        const all = Array.isArray(data) ? data : (data?.jobs || []);
        const filtered = all.filter(j =>
          j.technicianId === staff.staffId ||
          j.salesId === staff.staffId
        );
        setJobs(filtered);
      })
      .catch(() => setJobs([]))
      .finally(() => setLoading(false));
  }, [staff, dateRange.start, dateRange.end]);

  if (loading) return <div className="py-8 text-center text-gray-400">Yükleniyor...</div>;

  const completed = jobs.filter(j => ['teslim_edildi', 'completed'].includes(j.status)).length;
  const active = jobs.filter(j => !['teslim_edildi', 'completed', 'cancelled'].includes(j.status)).length;
  const totalRevenue = jobs.reduce((s, j) => s + Number(j.totalPrice || 0), 0);

  const serviceBreakdown = {};
  jobs.forEach(j => {
    (j.services || []).forEach(s => {
      const name = typeof s === 'string' ? s : (s.name || 'Bilinmeyen');
      serviceBreakdown[name] = (serviceBreakdown[name] || 0) + 1;
    });
  });

  return (
    <div className="space-y-4" data-testid="staff-performance-detail">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Toplam İş" value={jobs.length} />
        <StatTile label="Tamamlanan" value={completed} />
        <StatTile label="Aktif" value={active} />
        <StatTile label="Toplam Ciro" value={`${new Intl.NumberFormat('tr-TR').format(totalRevenue)} TL`} />
      </div>

      <div>
        <h4 className="font-display font-semibold text-sm mb-2 text-gray-400 uppercase tracking-wider">
          Hizmet Kırılımı
        </h4>
        <div className="space-y-1.5">
          {Object.entries(serviceBreakdown).sort((a, b) => b[1] - a[1]).map(([name, count]) => (
            <div key={name} className="flex justify-between items-center py-1.5 px-3 rounded bg-gray-800/60">
              <span className="text-sm text-white">{name}</span>
              <span className="font-mono text-sm font-semibold text-white">{count}</span>
            </div>
          ))}
          {Object.keys(serviceBreakdown).length === 0 && (
            <div className="text-sm text-gray-400 py-4 text-center">
              Bu tarih aralığında iş yok
            </div>
          )}
        </div>
      </div>

      <div>
        <h4 className="font-display font-semibold text-sm mb-2 text-gray-400 uppercase tracking-wider">
          Son İşler
        </h4>
        <div className="space-y-1.5 max-h-60 overflow-y-auto">
          {jobs.slice(0, 10).map(j => (
            <div key={j.id} className="flex justify-between items-center py-2 px-3 rounded border border-gray-700/40">
              <div>
                <div className="font-mono text-xs text-gray-400">{j.carPlate}</div>
                <div className="text-sm text-white">{j.customerName}</div>
              </div>
              <span className="font-mono text-sm text-white">
                {new Intl.NumberFormat('tr-TR').format(Number(j.totalPrice || 0))} TL
              </span>
            </div>
          ))}
          {jobs.length === 0 && (
            <div className="text-sm text-gray-400 py-4 text-center">İş bulunamadı</div>
          )}
        </div>
      </div>
    </div>
  );
}

const PerformanceTabContent = ({ staffPerformance }) => {
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setDate(1)).toISOString().slice(0, 10),
    end: new Date().toISOString().slice(0, 10),
  });

  return (
    <div className="space-y-3">
      <h3 className="text-white font-semibold font-display text-sm">Personel Performans Ozeti</h3>
      {staffPerformance.length === 0 ? (
        <div className="text-center text-gray-400 py-8">Performans verisi yok</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {staffPerformance.map(p => (
            <Card key={p.staffId}
              className="bg-gray-800/60 border-gray-700/40 cursor-pointer hover:border-brand/50 transition-colors"
              onClick={() => setSelectedStaff(p)}
              data-testid={`performance-card-${p.staffId}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="text-white font-semibold font-display text-sm">{p.name}</h4>
                    <div className="flex items-center gap-2 mt-0.5">
                      <Badge className="bg-indigo-600 text-white text-[10px]">{ROLE_LABELS[p.role] || p.role}</Badge>
                      {p.primaryBranchName && <BranchBadge branchId={p.primaryBranchId} branchName={p.primaryBranchName} small />}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold font-mono text-white">{p.completedCount}</p>
                    <p className="text-gray-400 text-[10px]">Tamamlanan</p>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-2 text-center text-xs">
                  <div className="bg-gray-700/40 rounded-lg p-2">
                    <p className="text-brand font-bold font-mono text-lg">{p.activeCount}</p>
                    <p className="text-gray-400 text-[10px]">Aktif</p>
                  </div>
                  <div className="bg-gray-700/40 rounded-lg p-2">
                    <p className="text-blue-400 font-bold font-mono text-lg">{p.qcReachedCount}</p>
                    <p className="text-gray-400 text-[10px]">KK Gelen</p>
                  </div>
                  <div className="bg-gray-700/40 rounded-lg p-2">
                    <p className={`font-bold font-mono text-lg ${p.warningRate > 30 ? 'text-red-400' : p.warningRate > 10 ? 'text-yellow-400' : 'text-green-400'}`}>{p.warningRate}%</p>
                    <p className="text-gray-400 text-[10px]">Uyari</p>
                  </div>
                  <div className="bg-gray-700/40 rounded-lg p-2">
                    <p className="text-white font-bold font-mono text-lg">{p.avgDurationHours != null ? `${p.avgDurationHours}s` : '-'}</p>
                    <p className="text-gray-400 text-[10px]">Ort Sure</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Drill-down Modal */}
      <Dialog open={!!selectedStaff} onOpenChange={(open) => !open && setSelectedStaff(null)}>
        <DialogContent className="bg-gray-900 border-gray-700 max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-white">
              {selectedStaff?.name}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {ROLE_LABELS[selectedStaff?.role] || selectedStaff?.role} · {selectedStaff?.primaryBranchName || 'Tüm Şubeler'}
            </DialogDescription>
          </DialogHeader>

          <div className="flex gap-3 items-end border-y border-gray-700 py-3 my-2">
            <div className="space-y-1">
              <Label className="text-xs text-gray-400">Başlangıç</Label>
              <Input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange(p => ({ ...p, start: e.target.value }))}
                className="w-40 bg-gray-800 border-gray-700 text-white"
                data-testid="perf-date-start"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-gray-400">Bitiş</Label>
              <Input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange(p => ({ ...p, end: e.target.value }))}
                className="w-40 bg-gray-800 border-gray-700 text-white"
                data-testid="perf-date-end"
              />
            </div>
          </div>

          <StaffPerformanceDetail staff={selectedStaff} dateRange={dateRange} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PerformanceTabContent;
